import type { StateCreator } from 'zustand';
import type { ClearablePatch, Journey } from '@my-fullstack-app/itinerary-types';
import { applyPatch, generateId } from '../helpers';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty } from '../sync-bridge';
import { track } from '../../lib/analytics';

/**
 * CRUD for `Trip.journeys[]` — scheduled long-haul transit (flights,
 * trains, ferries, etc.) per ADR-0043 v2. Trip-scoped because a single
 * Journey can span multiple days; per-day rendering is derived from
 * origin/destination datetime in `buildDayTimeline`.
 *
 * Marks 'journeys' dirty (its own wire field at trip level, parallel
 * to `stays`).
 */
export interface JourneySlice {
  addJourney: (
    tripId: string,
    journey: Omit<Journey, 'id'> & { id?: string | undefined},
  ) => void;
  removeJourney: (tripId: string, journeyId: string) => void;
  updateJourney: (
    tripId: string,
    journeyId: string,
    patch: ClearablePatch<Omit<Journey, 'id'>>,
  ) => void;
}

export const createJourneySlice: StateCreator<ItineraryState, [], [], JourneySlice> = (set, get) => ({
  addJourney: (tripId, journeyInput) => {
    get()._capture();
    const journey: Journey = { ...journeyInput, id: journeyInput.id ?? generateId() };
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return { ...t, journeys: [...(t.journeys ?? []), journey] };
      }),
    }));
    markTripDirty(tripId, ['journeys']);
    track('journey.added', {
      tripId,
      mode: journey.mode,
      hasBooking: !!journey.booking,
      hasCost: (journey.booking?.costs?.length ?? 0) > 0,
    });
  },

  removeJourney: (tripId, journeyId) => {
    get()._capture();
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return { ...t, journeys: (t.journeys ?? []).filter((j) => j.id !== journeyId) };
      }),
    }));
    markTripDirty(tripId, ['journeys']);
  },

  updateJourney: (tripId, journeyId, patch) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          journeys: (t.journeys ?? []).map((j) =>
            j.id === journeyId ? applyPatch({ ...j }, patch) : j,
          ),
        };
      }),
    }));
    markTripDirty(tripId, ['journeys']);
  },
});
